/**
 * Shared helpers for the events overview page (list/calendar/grid views).
 */

export const GRID_START_HOUR = 8;
export const GRID_END_HOUR = 22;

export const groupEventsByDate = (events) => {
  const map = new Map();
  (events || []).forEach((event) => {
    const key = new Date(event.start_time).toDateString();
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(event);
  });
  return map;
};

export const getEventStatus = (event, now = new Date()) => {
  const start = new Date(event.start_time);
  const end = new Date(event.end_time);
  if (now >= start && now <= end) return 'active';
  if (now > end) return 'past';
  return 'upcoming';
};

export const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', hour12: false });

export const formatDateHeading = (date) =>
  date.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Assigns each event in a single day to a "lane" so overlapping events
 * can be rendered side-by-side instead of stacked on top of each other.
 */
export const layoutDayEvents = (dayEvents) => {
  const sorted = [...(dayEvents || [])].sort(
    (a, b) => new Date(a.start_time) - new Date(b.start_time)
  );

  const laneEndTimes = [];
  const placements = sorted.map((event) => {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);

    let laneIndex = laneEndTimes.findIndex((endTime) => endTime <= start);
    if (laneIndex === -1) {
      laneIndex = laneEndTimes.length;
      laneEndTimes.push(end);
    } else {
      laneEndTimes[laneIndex] = end;
    }

    return { event, laneIndex };
  });

  const laneCount = Math.max(1, laneEndTimes.length);
  return placements.map((placement) => ({ ...placement, laneCount }));
};

/**
 * Minutes elapsed since GRID_START_HOUR, clamped to the visible grid window,
 * so 1 minute maps to 1px when GRID_END_HOUR - GRID_START_HOUR hours are shown.
 */
export const minutesFromGridStart = (date) => {
  const hours = date.getHours() + date.getMinutes() / 60;
  const clamped = Math.min(Math.max(hours, GRID_START_HOUR), GRID_END_HOUR);
  return (clamped - GRID_START_HOUR) * 60;
};

export const startOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

export const isSameDay = (a, b) => a.toDateString() === b.toDateString();
